/**
 * Content-agnostic metered-unlock primitives — the period key, the derived
 * meter, and the grow-only unlock reducer. Articles consume these today
 * (@learnzapp/core/articles); future metered content types (questions,
 * flashcards, …) import the SAME primitives with their own unlock map and
 * per-type brand flags — per-content-type pools, never a shared one.
 *
 * Model: free users spend from a per-period meter to open locked items. An
 * unlock is PERMANENT — the unlock map only grows, and the meter is derived
 * per period from the entries' `period_key` (no reset writes, no cleanup job,
 * deterministic offline). A cross-device merge race can therefore only grant
 * extra access, never revoke it. Merges MUST be unions.
 *
 * Pure and platform-agnostic: wall-clock time enters ONLY through the injected
 * `nowMs` / `periodKey` inputs (never read internally), so the same inputs
 * always yield the same decision — golden-testable, safe to run per render.
 *
 * CROSS-APP DETERMINISM: both apps MUST derive the period key via
 * `getUnlockPeriodKey`. A local-time or locale-calendar variant would split
 * the meter across platforms at period boundaries (UTC Monday for 'week',
 * UTC midnight for 'day').
 */

import moment from 'moment';

/** Tunable meter defaults — brands override both via config (e.g.
 *  `METERED_ARTICLE_UNLOCKS` / `METERED_ARTICLE_PERIOD`); the derivation
 *  functions take them per call. */
export const METER_DEFAULTS = {
  /** Unlocks per period, spendable on any domain. */
  UNLOCK_LIMIT: 3,
  /** Reset cadence: 'week' (UTC ISO week) or 'day' (UTC calendar day). */
  PERIOD: 'week',
};

/** Sentinel `period_key` for migration-granted unlocks. Entries with this key
 *  never count against any period's meter. */
export const LEGACY_PERIOD_KEY = 'legacy';

/**
 * Deterministic period key:
 *  - 'week' → UTC ISO-8601 week, e.g. '2026-W29'. `GGGG` is the ISO week-year
 *    (year-boundary safe: 2027-01-01 is '2026-W53'); isoWeek is Monday-start
 *    and locale-independent.
 *  - 'day'  → UTC calendar date, e.g. '2026-07-18' (resets at UTC midnight).
 * An unknown period falls back to 'week' so a misconfigured brand degrades to
 * the least-generous known cadence instead of bricking the meter.
 *
 * @param {number} nowMs epoch ms (inject `Date.now()` at the call site)
 * @param {'week'|'day'} [period='week']
 * @returns {string}
 */
export function getUnlockPeriodKey(nowMs, period = 'week') {
  if (period === 'day') return moment.utc(nowMs).format('YYYY-MM-DD');
  return moment.utc(nowMs).format('GGGG-[W]WW');
}

/** Type-insensitive map lookup: object keys are strings, ids arrive as either. */
export function meterEntryFor(map, id) {
  if (map == null || id == null) return undefined;
  return map[String(id)];
}

/**
 * Derive the current period's meter usage from the grow-only unlock map.
 * Entries keyed in a DIFFERENT period style (e.g. week keys after a brand
 * switches to daily) simply never match — they stay permanent unlocks but
 * never count, so a period change can only grant, never revoke.
 *
 * @param {Object} unlockMap { [id]: { period_key, unlocked_at, source } }
 * @param {string} periodKey from getUnlockPeriodKey
 * @param {number} [limit]
 * @returns {{ used: number, remaining: number, limit: number, periodKey: string }}
 */
export function getMeterState(unlockMap, periodKey, limit = METER_DEFAULTS.UNLOCK_LIMIT) {
  const used = Object.values(unlockMap ?? {}).filter(
    (e) => e && e.period_key === periodKey && e.period_key !== LEGACY_PERIOD_KEY
  ).length;
  return { used, remaining: Math.max(0, limit - used), limit, periodKey };
}

/**
 * Pure unlock reducer — returns the NEXT unlock map (never mutates the input).
 * Idempotent: an already-present id is a no-op (`changed: false`), so a
 * re-unlock can never double-spend the meter.
 *
 * @param {Object} unlockMap
 * @param {string|number} id
 * @param {string} periodKey current period (or LEGACY_PERIOD_KEY for migration grants)
 * @param {string} nowIso   ISO timestamp (inject at the call site)
 * @param {string} source   surface tag, e.g. 'topic_list'|'related_rail'|'pager'|'quick_set'|'legacy_migration'
 * @returns {{ unlockMap: Object, changed: boolean }}
 */
export function applyUnlock(unlockMap, id, periodKey, nowIso, source) {
  const map = unlockMap ?? {};
  if (id == null || meterEntryFor(map, id)) {
    return { unlockMap: map, changed: false };
  }
  return {
    unlockMap: {
      ...map,
      [String(id)]: { period_key: periodKey, unlocked_at: nowIso, source },
    },
    changed: true,
  };
}

/**
 * The period-dependent copy nouns, pinned in ONE place so web and mobile can't
 * drift and the copy rule holds: say "resets weekly"/"resets daily" — NEVER
 * name a day or hour (UTC boundaries land mid-afternoon in US time zones).
 *
 * @param {'week'|'day'} [period='week']
 * @returns {{ thisPeriod: string, resetsAdverb: string, everyPeriod: string, statLabel: string }}
 */
export function meterPeriodCopy(period = 'week') {
  if (period === 'day') {
    return { thisPeriod: 'today', resetsAdverb: 'daily', everyPeriod: 'every day', statLabel: 'Unlocks Today' };
  }
  return { thisPeriod: 'this week', resetsAdverb: 'weekly', everyPeriod: 'every week', statLabel: 'Unlocks This Week' };
}
